/**
 * Patient Form Page
 * Page for creating or editing patients
 */

import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  usePatientQuery,
  useCreatePatient,
  useUpdatePatient,
} from '../hooks/usePatients';
import { PatientForm, PatientFormData } from '../components/patients/PatientForm';
import { LoadingSpinner } from '../components/LoadingSpinner';

export function PatientFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const isEditMode = id !== 'new';
  const patientId = isEditMode ? parseInt(id!) : undefined;

  // Queries
  const { data: patient, isLoading: patientLoading } = usePatientQuery(patientId);

  // Mutations
  const createPatient = useCreatePatient();
  const updatePatient = useUpdatePatient(patientId!);

  const handleSubmit = async (data: PatientFormData) => {
    try {
      if (isEditMode && patientId) {
        await updatePatient.mutateAsync(data);
        navigate(`/patients/${patientId}`);
      } else {
        const result = await createPatient.mutateAsync(data);
        navigate(`/patients/${result.id}`);
      }
    } catch (error) {
      console.error('Failed to save patient:', error);
      // Error handling could be improved with toast notifications
    }
  };

  const handleCancel = () => {
    if (isEditMode && patientId) {
      navigate(`/patients/${patientId}`);
    } else {
      navigate('/patients');
    }
  };

  if (isEditMode && patientLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner text="Hleð sjúklingi..." size="lg" />
      </div>
    );
  }

  if (isEditMode && !patient) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Sjúklingur fannst ekki</h2>
        <button
          onClick={() => navigate('/patients')}
          className="mt-4 text-brand-primary hover:underline"
        >
          Fara til baka
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleCancel}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Breyta sjúklingi' : 'Nýr sjúklingur'}
          </h1>
          <p className="text-gray-600">
            {isEditMode
              ? `Uppfæra upplýsingar fyrir ${patient?.first_name} ${patient?.last_name}`
              : 'Skrá nýjan sjúkling í kerfið'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <PatientForm
          patient={patient}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={createPatient.isPending || updatePatient.isPending}
          mode={isEditMode ? 'edit' : 'create'}
        />
      </div>
    </div>
  );
}
